import React, { useEffect, useState } from "react";
import { useDashboard } from "../context/DashboardContext";
import api from "../lib/api";
import { PageHeader, StatCard } from "../components/common/PageParts";
import { ShieldCheck, Calculator } from "lucide-react";
import { fmtPrice } from "../lib/format";

export default function RiskCenter() {
  const { activeSymbol } = useDashboard();
  const [analysis, setAnalysis] = useState(null);
  const [swing, setSwing] = useState(null);
  const [capital, setCapital] = useState(10000);
  const [riskPct, setRiskPct] = useState(1);

  useEffect(() => {
    // Swing context is 4H (not hardcoded 1h). Locked plan is the source of truth.
    api.getAnalysis(activeSymbol, "4h").then(setAnalysis).catch(() => {});
    api.getSwing(activeSymbol).then(setSwing).catch(() => {});
  }, [activeSymbol]);

  const locked = swing?.active && swing.active.entryLow != null ? swing.active : null;
  const snap = analysis?.plan;
  const price = analysis?.price || 0;
  // Chart, Planner and Risk Center all use the SAME locked plan when one exists.
  const usingLocked = !!locked;
  const planId = usingLocked ? locked.planId : null;
  const entry = usingLocked ? (locked.entryLow + locked.entryHigh) / 2
                            : (snap?.entryLow ? (snap.entryLow + snap.entryHigh) / 2 : price);
  const stop = usingLocked ? locked.stopLoss : snap?.stopLoss;
  const rr = usingLocked ? (locked.rr?.tp2 ?? locked.rr?.tp1) : snap?.riskReward;
  const riskAmount = capital * (riskPct / 100);
  const perUnitRisk = stop ? Math.abs(entry - stop) : 0;
  const positionSize = perUnitRisk ? riskAmount / perUnitRisk : 0;
  const notional = positionSize * entry;

  const conf = analysis?.confluence ?? 0;
  const protection = conf < 45;
  const latestDir = analysis?.direction;
  const conflict = usingLocked && ["LONG", "SHORT"].includes(latestDir) && latestDir !== locked.direction;
  const subtitle = usingLocked
    ? `Position sizing on locked ${locked.direction} plan ${planId} (4H)`
      + (conflict ? ` · CONFLICT: latest 4H bias is ${latestDir} — plan stays locked, not auto-flipped` : "")
    : `Position sizing & capital protection for ${activeSymbol} (4H mechanical snapshot — no locked plan)`;

  // P2A-1: validate inputs before showing any sizing number.
  const planDir = usingLocked ? locked.direction : analysis?.direction;
  const capitalOk = capital > 0;
  const riskOk = riskPct > 0 && riskPct <= 10;
  const stopOk = stop != null && ((planDir === "LONG" && stop < entry) || (planDir === "SHORT" && stop > entry));
  const sizeOk = perUnitRisk > 0 && positionSize > 0;
  const inputValid = capitalOk && riskOk && stopOk && sizeOk;
  const reasons = [];
  if (!capitalOk) reasons.push("capital must be > 0");
  if (!riskOk) reasons.push("risk % must be within 0–10");
  if (!stopOk) reasons.push("stop-loss missing or on wrong side of entry");
  if (capitalOk && riskOk && stopOk && !sizeOk) reasons.push("position size not computable");
  const reasonText = reasons.join("; ");
  const blocked = !inputValid;
  // Capital Protection Mode = valid inputs AND valid SL AND valid size, THEN confluence.
  const modeColor = blocked ? "var(--red)" : protection ? "var(--zec)" : "var(--green)";
  const modeBg = blocked ? "rgba(239,68,68,.08)" : protection ? "rgba(34,211,238,.08)" : "rgba(34,197,94,.06)";
  const modeBorder = blocked ? "rgba(239,68,68,.3)" : protection ? "rgba(34,211,238,.3)" : "rgba(34,197,94,.25)";
  const modeIconBg = blocked ? "rgba(239,68,68,.14)" : protection ? "rgba(34,211,238,.14)" : "rgba(34,197,94,.12)";
  const modeLabel = blocked ? "SIZING DIBLOKIR" : protection ? "PROTECTION ACTIVE" : "TRADING AKTIF";
  const modeMsg = blocked
    ? `Sizing unavailable — invalid input (${reasonText}).`
    : protection
      ? "Confluence below 45 — engine recommends NO TRADE and prioritises capital preservation."
      : "Konfluensi mendukung entry selektif dalam risiko yang ditentukan.";

  return (
    <div className="ym-fade">
      <PageHeader title="Pusat Risiko" subtitle={subtitle} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Modal Akun" value={capitalOk ? `$${capital.toLocaleString()}` : "—"} />
        <StatCard label="Risiko / Trade" value={`${riskPct}%`} sub={riskOk ? `$${riskAmount.toFixed(2)} at risk` : "invalid risk %"} color="var(--amber)" />
        <StatCard label="R:R (plan)" value={rr ? `1 : ${rr} (constructed)` : "—"} color="var(--accent-soft)" />
        <StatCard label="Confluence" value={`${conf}/100`} color={protection ? "var(--red)" : "var(--green)"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ym-panel p-4">
          <div className="flex items-center gap-2 mb-4"><Calculator size={16} style={{ color: "var(--accent-soft)" }} /><h3 className="text-[13px] font-bold">Kalkulator Ukuran Posisi</h3></div>
          <Field label="Modal Akun (USDT)" value={capital} onChange={setCapital} />
          <Field label="Risiko per Trade (%)" value={riskPct} onChange={setRiskPct} step="0.1" />
          <div className="mt-4 space-y-2">
            <Res k="Entry (tengah)" v={`$${fmtPrice(entry)}`} />
            <Res k="Stop Loss" v={stop ? `$${fmtPrice(stop)}` : "—"} c="var(--red)" />
            <Res k="Risiko / unit" v={inputValid ? `$${fmtPrice(perUnitRisk)}` : "—"} />
            <Res k="Ukuran Posisi" v={inputValid ? `${positionSize.toFixed(4)} ${activeSymbol.replace("USDT", "")}` : "Sizing unavailable — invalid input"} c="var(--green)" bold />
            <Res k="Nilai Nosional" v={inputValid ? `$${notional.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"} c="var(--accent-soft)" bold />
          </div>
        </div>

        <div className="ym-panel p-4 relative overflow-hidden">
          <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} style={{ color: modeColor }} /><h3 className="text-[13px] font-bold">Mode Perlindungan Modal</h3></div>
          <div className="rounded-xl p-5 text-center" style={{ background: modeBg, border: `1px solid ${modeBorder}` }}>
            <div className="grid place-items-center mx-auto mb-3 rounded-2xl" style={{ width: 64, height: 64, background: modeIconBg }}>
              <ShieldCheck size={32} style={{ color: modeColor }} />
            </div>
            <div className="text-[18px] font-extrabold" style={{ color: modeColor }}>
              {modeLabel}
            </div>
            <p className="text-[12px] mt-1.5" style={{ color: "var(--text-muted)" }}>
              {modeMsg}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, step = "1" }) {
  return (
    <div className="mb-3">
      <label className="text-[11px] font-semibold" style={{ color: "var(--text-muted)" }}>{label}</label>
      <input type="number" step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1 rounded-lg px-3 py-2 text-[13px] tabnum outline-none"
        style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
    </div>
  );
}
function Res({ k, v, c, bold }) {
  return (
    <div className="flex justify-between rounded-lg px-3 py-2" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <span className="text-[12px]" style={{ color: "var(--text-muted)" }}>{k}</span>
      <span className={`tabnum text-[12px] ${bold ? "font-extrabold" : "font-semibold"}`} style={{ color: c || "var(--text)" }}>{v}</span>
    </div>
  );
}
